import { router } from './admin-router.js';
import { store } from './admin-store.js';
import { api } from './admin-api.js';
import { eventBus } from './events.js';
import { renderLayout, getContentEl } from './components/admin-layout.js';
import { getMe, isAuthenticated, logout } from './modules/auth/auth-service.js';
import { initCommandPalette } from './components/command-palette.js';
import { initKeyboardShortcuts } from './components/keyboard-shortcuts.js';
import { initTheme } from './components/admin-header.js';

// Route definitions
const routes = [
    { path: '/login', handler: (c) => import('./modules/auth/login.js') },
    { path: '/', handler: (c) => import('./modules/dashboard/index.js') },
    { path: '/accounts', handler: (c) => import('./modules/accounts/index.js') },
    { path: '/accounts/:id', handler: (c) => import('./modules/accounts/show.js') },
    { path: '/appointments', handler: (c) => import('./modules/appointments/index.js') },
    { path: '/visits', handler: (c) => import('./modules/visits/index.js') },
    { path: '/transactions', handler: (c) => import('./modules/transactions/index.js') },
    { path: '/billing', handler: (c) => import('./modules/billing/index.js') },
    { path: '/feature-labels', handler: (c) => import('./modules/feature-labels/index.js') },
    { path: '/stats', handler: (c) => import('./modules/stats/index.js') },
    { path: '/roles', handler: (c) => import('./modules/roles/index.js') },
    { path: '/settings', handler: (c) => import('./modules/settings/index.js') },
    { path: '/system', handler: (c) => import('./modules/system/index.js') },
    { path: '/admins', handler: (c) => import('./modules/admins/index.js') },
    { path: '/audit', handler: (c) => import('./modules/audit/index.js') },
    { path: '/users', handler: (c) => import('./modules/users/index.js') },
    { path: '/users/:id', handler: (c) => import('./modules/users/index.js') },
    { path: '/calls', handler: (c) => import('./modules/calls/index.js') },
    { path: '/bots', handler: (c) => import('./modules/bots/index.js') },
    { path: '/unified-bot', handler: (c) => import('./modules/unified-bot/index.js') },
    { path: '/reviews', handler: (c) => import('./modules/reviews/index.js') },
    { path: '/traffic', handler: (c) => import('./modules/traffic/index.js') },
    { path: '/partners', handler: (c) => import('./modules/partners/index.js') },
    { path: '/seo', handler: (c) => import('./modules/seo/index.js') },
    { path: '/mail', handler: (c) => import('./modules/mail/index.js') },
    { path: '/map', handler: (c) => import('./modules/map/index.js') },
    { path: '/market', handler: (c) => import('./modules/market/index.js') },
    { path: '/dgdat', handler: (c) => import('./modules/dgdat/index.js') },
    { path: '/cluster', handler: (c) => import('./modules/cluster/index.js') },
    { path: '/perf', handler: (c) => import('./modules/perf/index.js') },
    // External payees (superadmin-only on backend side)
    { path: '/payees', handler: (c, ctx) => import('./modules/payees/index.js').then(m => m.default(c, ctx)) },
    { path: '/payees/create', handler: (c, ctx) => import('./modules/payees/index.js').then(m => m.default(c, ctx)) },
    { path: '/payees/:id', handler: (c, ctx) => import('./modules/payees/index.js').then(m => m.default(c, ctx)) },
    { path: '/payees/:id/:tab', handler: (c, ctx) => import('./modules/payees/index.js').then(m => m.default(c, ctx)) },
    // Ops Cockpit (superadmin-only) — provider/asset/calendar/alerts inventory
    { path: '/ops', handler: (c) => import('./modules/ops/index.js').then(m => m.renderDashboard(c)) },
    { path: '/ops/accounts', handler: (c) => import('./modules/ops/index.js').then(m => m.renderAccountsList(c)) },
    { path: '/ops/accounts/:id', handler: (c, ctx) => import('./modules/ops/index.js').then(m => m.renderAccountDetail(c, ctx)) },
    { path: '/ops/calendar', handler: (c) => import('./modules/ops/index.js').then(m => m.renderCalendar(c)) },
    { path: '/ops/events', handler: (c) => import('./modules/ops/index.js').then(m => m.renderEvents(c)) },
    // Ops SEO Analytics (S2-SEO Phase I)
    { path: '/ops/seo', handler: (c, ctx) => import('./modules/ops/index.js').then(m => m.renderSeoOverview(c, ctx)) },
    { path: '/ops/seo/site/:domain', handler: (c, ctx) => import('./modules/ops/index.js').then(m => m.renderSeoSiteDetail(c, ctx)) },
    { path: '/ops/seo/site/:domain/template/:label', handler: (c, ctx) => import('./modules/ops/index.js').then(m => m.renderSeoTemplateDetail(c, ctx)) },
    { path: '/ops/seo/site/:domain/core', handler: (c, ctx) => import('./modules/ops/index.js').then(m => m.renderSeoCoreSurface(c, ctx)) },
];

// Auth middleware
function authMiddleware(ctx) {
    const publicRoutes = ['/login'];
    if (publicRoutes.includes(ctx.path)) {
        // If already authenticated, redirect to dashboard
        if (store.user) {
            router.navigate('/');
            return false;
        }
        return true;
    }

    if (!store.user) {
        router.navigate('/login');
        return false;
    }
    return true;
}

// Layout middleware
function layoutMiddleware(ctx) {
    renderLayout();
    const contentEl = getContentEl();
    if (contentEl) {
        router._container = contentEl;
    }
    return true;
}

// Event handlers
eventBus.on('auth:logout', async () => {
    await logout();
    renderLayout();
    router.navigate('/login');
});

eventBus.on('auth:login', () => {
    renderLayout();
});

// Init
async function init() {
    // Try to restore session
    if (isAuthenticated()) {
        try {
            const user = await getMe();
            store.user = user;
        } catch {
            api.clearTokens();
            store.user = null;
        }
    }

    // Register middlewares
    router.use(authMiddleware);
    router.use(layoutMiddleware);

    // Register routes
    for (const route of routes) {
        router.add(route.path, route.handler);
    }

    // Initialize global features
    initTheme();
    initCommandPalette();
    initKeyboardShortcuts();

    // Start router with a temporary container (layout middleware will swap it)
    const app = document.getElementById('app');
    router.start(app);

    // Remove preloader
    const preloader = document.getElementById('preloader');
    if (preloader) {
        preloader.classList.add('fade-out');
        setTimeout(() => preloader.remove(), 400);
    }
}

init().catch(err => {
    console.error('Admin init error:', err);
    const preloader = document.getElementById('preloader');
    if (preloader) preloader.remove();
});
